// /business-profile-advisor/search-places

import { toast } from "react-toastify";
import { ApiService } from "../../../config";

import { handleError } from "../../../utils/parseApiReturn";
import {
  INewGeoRankingPayload,
  IPlan,
  IRecommendedBriefingPayload,
  IRecommendedCategoriesPayload,
  IRecommendedKeywordsPayload,
  ISearchedProfile,
  ICheckoutData,
} from "../types";
import { AxiosRequestConfig } from "axios";

type SearchPlacesOptions = {
  silent?: boolean;
};

const getBearerConfig = (token?: string): AxiosRequestConfig | undefined => {
  if (!token) {
    return undefined;
  }

  return {
    headers: { Authorization: `Bearer ${token}` },
  };
};

const createGeoRankingCampaign = async (
  payload: INewGeoRankingPayload,
  token: string
): Promise<any> => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    // Ajuste o endpoint '/georanking/campaign' conforme a sua API
    const response = await ApiService.post("/checkout", payload, config);

    const { message, error, data } = response.data;
    if (!error) {
      return { success: true, data };
    } else {
      return { success: false, message };
    }
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível realizar o cadastro."
    );
    toast.error(message);
    return false;
  }
};

const getPlans = async (token: string): Promise<IPlan[]> => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await ApiService.get("/plans", config);
    return response.data.data.filter((plan: any) => !plan.indDeleted);
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível carregar os planos."
    );
    toast.error(message);
    return [];
  }
};

const getCategories = (allCategories: any) => {
  const categories: string[] = [];
  categories.push(allCategories.primarySuggestion.category);
  allCategories.additionalSuggestion.forEach((cat: any) => {
    categories.push(cat.category);
  });
  return categories;
};

const getRecommendedCategories = async (
  payload: IRecommendedCategoriesPayload,
  token: string
): Promise<string[]> => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await ApiService.post(
      "/business-profile-advisor/recommended-categories",
      payload,
      config
    );
    return getCategories(response.data.data) || [];
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível obter as categorias recomendadas."
    );
    toast.error(message);
    return [];
  }
};

const getRecommendedKeywords = async (
  payload: IRecommendedKeywordsPayload,
  token: string
): Promise<string[]> => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await ApiService.post(
      "/business-profile-advisor/recommended-keywords",
      payload,
      config
    );
    return response.data.data.keywords || [];
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível obter as palavras-chave recomendadas."
    );
    toast.error(message);
    return [];
  }
};

const getRecommendedBriefing = async (
  payload: IRecommendedBriefingPayload,
  token: string
): Promise<string> => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await ApiService.post(
      "/business-profile-advisor/recommended-briefing",
      payload,
      config
    );
    return response.data.data || "";
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível gerar o resumo recomendado."
    );
    toast.error(message);
    return "";
  }
};

const getAvailableProfiles = async (token: string): Promise<ISearchedProfile[]> => {
  try {
    const config: AxiosRequestConfig = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const response = await ApiService.get(
      `/business-profile-advisor/available-profiles`,
      config
    );
    return response.data.data?.profiles || [];
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível carregar os perfis disponíveis."
    );
    toast.error(message);
    return [];
  }
};

const searchPlacesByName = async (
  placeName: string,
  token?: string,
  options?: SearchPlacesOptions,
): Promise<ISearchedProfile[]> => {
  if (!placeName.trim()) {
    return token ? getAvailableProfiles(token) : [];
  }

  try {
    const response = await ApiService.get(
      `/business-profile-advisor/places?name=${encodeURIComponent(placeName)}`,
      getBearerConfig(token),
    );
    return response.data.data?.profiles || [];
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível buscar os perfis relacionados."
    );
    if (!options?.silent) {
      toast.error(message);
    }
    return [];
  }
};

const searchCategories = async (
  searchText: string,
  token: string
): Promise<{gcid: string, category: string}[]> => {
  if (!searchText.trim()) {
    return [];
  }
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await ApiService.get(
      `/business-profile-advisor/searchCategory?input=${searchText}`,
      config
    );
    return response.data.data || [];
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível buscar as categorias."
    );
    toast.error(message);
    return [];
  }
};

const getCheckoutData = async (token: string): Promise<ICheckoutData | null> => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await ApiService.get("/checkout", config);
    return response.data?.data || null;
  } catch (error) {
    const { message } = handleError(
      error,
      "Não foi possível carregar os dados do checkout."
    );
    toast.error(message);
    return null;
  }
};


export const GeoRankingService = {
  createGeoRankingCampaign,
  getPlans,
  getRecommendedCategories,
  getRecommendedKeywords,
  getRecommendedBriefing,
  getAvailableProfiles,
  searchPlacesByName,
  searchCategories,
  getCheckoutData,
};
